"use client";

import { Review } from "@/lib/types/review";
import { useGSAP } from "@gsap/react";
import { SourceCodeIcon, TimerIcon, ZapIcon } from "@hugeicons/core-free-icons";
import gsap from "gsap";
import { useRef } from "react";
import { ReviewCard } from "../review-card";

gsap.registerPlugin(useGSAP);

const stats = [
  {
    icon: ZapIcon,
    value: "100 / 100",
    label: "Lighthouse Score",
    position: "top-[15%] left-[8%]",
  },
  {
    icon: TimerIcon,
    value: "< 1s",
    label: "Average Load Time",
    position: "top-[42%] right-[6%]",
  },
  {
    icon: SourceCodeIcon,
    value: "Hand-Coded",
    label: "No page builders",
    position: "bottom-[18%] left-[14%]",
  },
];

export function HeroVisual({
  reviewPreviews,
}: {
  reviewPreviews: Omit<Review, "text">[];
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      // Hide cards initially to prevent flash before animation
      gsap.set(".hero-stat-card", { autoAlpha: 0, y: 20, filter: "blur(8px)" });

      // Entry animation, delayed to start after HeroText finishes (~1.7s)
      const tl = gsap.timeline({ delay: 1.7 });

      tl.to(".hero-stat-card", {
        autoAlpha: 1,
        y: 0,
        filter: "blur(0px)",
        duration: 0.5,
        stagger: 0.15,
        ease: "power2.out",
      });

      // After entry, start an independent repeating tween per card so they
      // never compete for state. Slightly different durations make it organic.
      tl.call(
        () => {
          gsap.utils
            .toArray<HTMLElement>(".hero-stat-card")
            .forEach((card, i) => {
              gsap.fromTo(
                card,
                { y: 0 },
                {
                  y: 12,
                  duration: 2.2 + i * 0.3,
                  ease: "sine.inOut",
                  yoyo: true,
                  repeat: -1,
                  delay: i * 0.35,
                },
              );
            });
        },
        undefined,
        "+=0.1",
      );
    },
    { scope: containerRef },
  );

  return (
    <div
      ref={containerRef}
      className="relative hidden xl:flex items-center justify-center"
    >
      {/* Floating stat cards */}
      {reviewPreviews.map((review, i) => (
        <div
          key={review._id}
          className={`hero-stat-card invisible absolute ${stats[i].position}`}
        >
          <ReviewCard review={review} />
        </div>
      ))}
    </div>
  );
}
